// Single fetch wrapper around the b.jwk.dk headless WordPress REST API,
// with an in-memory cache keyed by the exact endpoint URL requested, plus
// typed getters on top that ask for only the fields each view needs.
//
// Matches the actual `project` CPT schema registered by the jwk-portfolio
// plugin (wp-content/plugins/jwk-portfolio): no taxonomies, no gallery —
// just title/slug/content plus four post-meta fields (_jwk_image,
// _jwk_start_date, _jwk_end_date, _jwk_link) and the REST-only
// `image_details` convenience field.

export const API_ORIGIN = 'https://b.jwk.dk';
const API_BASE = `${API_ORIGIN}/wp-json/wp/v2`;

const cache = new Map();

export async function fetchFromAPI(endpoint) {
  if (cache.has(endpoint)) return cache.get(endpoint);

  const promise = fetch(`${API_BASE}${endpoint}`)
    .then((res) => {
      if (!res.ok) throw new Error(`API ${endpoint} -> ${res.status}`);
      return res.json();
    })
    .catch((err) => {
      cache.delete(endpoint); // don't cache a failed request
      throw err;
    });

  cache.set(endpoint, promise);
  return promise;
}

const LIST_FIELDS = 'id,slug,title,excerpt,meta,image_details';
const DETAIL_FIELDS = 'id,slug,title,content,block_css,meta,image_details';

// Card-grid views: trimmed fields, reuse a broader cached response
// instead of re-fetching if one's already in flight/resolved.
export async function getProjects() {
  return fetchFromAPI(`/project?_fields=${LIST_FIELDS}&per_page=100`);
}

const projectBySlugEndpoint = (slug) =>
  `/project?slug=${encodeURIComponent(slug)}&_fields=${DETAIL_FIELDS}`;

export async function getProjectBySlug(slug) {
  const results = await fetchFromAPI(projectBySlugEndpoint(slug));
  return results[0] || null;
}

// --- WordPress Pages ---------------------------------------------------
// Every published Page becomes a frontend route, built dynamically at
// startup — see app.js's buildPageRoutes() and portfolio-spa-plan.md §2a.
// This is the whole point of the "everything from the backend" change:
// no route's content is hardcoded in the SPA, and a new Page in wp-admin
// becomes a new live URL with no code change.

const PAGE_LIST_FIELDS = 'id,slug,title,menu_order';
// block_css: per-page layout CSS WordPress generates for core blocks
// (wp-container-* rules) — see the jwk-portfolio plugin's class-core-blocks.php.
const PAGE_DETAIL_FIELDS = 'id,slug,title,content,block_css';

// orderby=menu_order lets Jonas control route/nav ordering from wp-admin's
// Page Attributes box instead of getting whatever order the API returns.
const pagesEndpoint = () =>
  `/pages?_fields=${PAGE_LIST_FIELDS}&per_page=100&orderby=menu_order&order=asc`;
const pageBySlugEndpoint = (slug) =>
  `/pages?slug=${encodeURIComponent(slug)}&_fields=${PAGE_DETAIL_FIELDS}`;

export async function getPages() {
  return fetchFromAPI(pagesEndpoint());
}

export async function getPageBySlug(slug) {
  const results = await fetchFromAPI(pageBySlugEndpoint(slug));
  return results[0] || null;
}

// --- Full request URLs, for build.js's <link rel="preload" as="fetch"> --
// build.js imports these so the preload hints it writes into index.html
// are byte-identical to what the getters above actually fetch. If they
// drift even by parameter order, the browser can't reuse the preloaded
// response and downloads everything twice. Node-safe: nothing here
// touches the DOM or fetch at import time.
export const preloadUrls = {
  pages: () => `${API_BASE}${pagesEndpoint()}`,
  pageBySlug: (slug) => `${API_BASE}${pageBySlugEndpoint(slug)}`,
  projectBySlug: (slug) => `${API_BASE}${projectBySlugEndpoint(slug)}`,
};
